import React, { useState, useEffect } from "react";

const Aerators = () => {
  const [aerators, setAerators] = useState([
    {
      id: 1,
      name: "Paddle Wheel #1",
      status: "running",
      rpmCurrent: 28,
      rpmTarget: 30,
      waterTemp: 28.4,
      dissolvedOxygen: 6.2,
      efficiency: 94,
      lastMaintenance: "2025-08-12",
      nightMode: true,
    },
    {
      id: 2,
      name: "Paddle Wheel #2",
      status: "running",
      rpmCurrent: 30,
      rpmTarget: 30,
      waterTemp: 28.2,
      dissolvedOxygen: 6.8,
      efficiency: 98,
      lastMaintenance: "2025-09-03",
      nightMode: false,
    },
    {
      id: 3,
      name: "Paddle Wheel #3",
      status: "stopped",
      rpmCurrent: 0,
      rpmTarget: 30,
      waterTemp: 27.8,
      dissolvedOxygen: 4.1,
      efficiency: 0,
      lastMaintenance: "2025-07-28",
      nightMode: false,
      alertSince: "14:32",
    },
    {
      id: 4,
      name: "Paddle Wheel #4",
      status: "running",
      rpmCurrent: 29,
      rpmTarget: 30,
      waterTemp: 28.5,
      dissolvedOxygen: 7.1,
      efficiency: 96,
      lastMaintenance: "2025-09-10",
      nightMode: true,
    },
    {
      id: 5,
      name: "Submersible #1",
      status: "running",
      rpmCurrent: 1450,
      rpmTarget: 1500,
      waterTemp: 28.1,
      dissolvedOxygen: 6.5,
      efficiency: 95,
      lastMaintenance: "2025-08-25",
      nightMode: false,
    },
    {
      id: 6,
      name: "Submersible #2",
      status: "running",
      rpmCurrent: 1495,
      rpmTarget: 1500,
      waterTemp: 28.3,
      dissolvedOxygen: 6.9,
      efficiency: 99,
      lastMaintenance: "2025-09-08",
      nightMode: true,
    },
    {
      id: 7,
      name: "Linear #1",
      status: "warning",
      rpmCurrent: 22,
      rpmTarget: 30,
      waterTemp: 29.1,
      dissolvedOxygen: 5.8,
      efficiency: 74,
      lastMaintenance: "2025-07-15",
      nightMode: true,
      alertSince: "09:15",
    },
    {
      id: 8,
      name: "Linear #2",
      status: "running",
      rpmCurrent: 30,
      rpmTarget: 30,
      waterTemp: 28.0,
      dissolvedOxygen: 6.7,
      efficiency: 97,
      lastMaintenance: "2025-09-05",
      nightMode: false,
    },
  ]);

  const [selectedAerator, setSelectedAerator] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setAerators((prev) =>
        prev.map((aerator) => {
          const variance = 0.5 + Math.random() * 1.5;
          const tempVariance = (Math.random() - 0.5) * 0.8;
          const efficiencyVariance = Math.floor((Math.random() - 0.5) * 4);

          let newRpm = aerator.rpmCurrent;
          let newDO = aerator.dissolvedOxygen;
          let newTemp = aerator.waterTemp;
          let newEfficiency = aerator.efficiency;

          if (aerator.status === "running") {
            newRpm = Math.max(
              aerator.rpmTarget * 0.8,
              Math.min(
                aerator.rpmTarget * 1.05,
                aerator.rpmCurrent + (Math.random() - 0.5) * variance
              )
            );
            newDO = Math.max(
              3.5,
              Math.min(
                8.5,
                aerator.dissolvedOxygen + (Math.random() - 0.5) * 0.4
              )
            );
            newTemp = aerator.waterTemp + tempVariance;
            newEfficiency = Math.max(
              60,
              Math.min(
                100,
                aerator.efficiency + efficiencyVariance
              )
            );
          }

          return {
            ...aerator,
            rpmCurrent: Math.round(newRpm * 10) / 10,
            dissolvedOxygen: Math.round(newDO * 10) / 10,
            waterTemp: Math.round(newTemp * 10) / 10,
            efficiency: Math.round(newEfficiency),
          };
        })
      );
      setLastUpdate(new Date());
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const runningCount = aerators.filter((a) => a.status === "running").length;
  const warningCount = aerators.filter((a) => a.status === "warning").length;
  const stoppedCount = aerators.filter((a) => a.status === "stopped").length;
  const avgDO = (
    aerators.reduce((sum, a) => sum + a.dissolvedOxygen, 0) / aerators.length
  ).toFixed(1);
  const avgEfficiency = Math.round(
    aerators.reduce((sum, a) => sum + a.efficiency, 0) / aerators.length
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "running":
        return "bg-emerald-50 border-emerald-200 text-emerald-900";
      case "warning":
        return "bg-amber-50 border-amber-200 text-amber-900";
      case "stopped":
        return "bg-red-50 border-red-200 text-red-900";
      default:
        return "bg-gray-50 border-gray-200 text-gray-900";
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "running":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">
            ● Running
          </span>
        );
      case "warning":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded">
            ⚠ Warning
          </span>
        );
      case "stopped":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded">
            ✕ Stopped
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-5 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">
          Aerator Monitoring
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Real-time performance tracking across {aerators.length} units
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 px-6 py-5 bg-gray-50 border-b border-gray-200">
        <div className="flex flex-col">
          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
            Running
          </span>
          <span className="text-2xl font-bold text-emerald-600 mt-1">
            {runningCount}/{aerators.length}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
            Warnings
          </span>
          <span className="text-2xl font-bold text-amber-600 mt-1">
            {warningCount}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
            Stopped
          </span>
          <span className="text-2xl font-bold text-red-600 mt-1">
            {stoppedCount}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
            Avg Efficiency
          </span>
          <span className="text-2xl font-bold text-blue-600 mt-1">
            {avgEfficiency}%
          </span>
        </div>
      </div>

      <div className="px-6 py-5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {aerators.map((aerator) => (
            <div
              key={aerator.id}
              onClick={() =>
                setSelectedAerator(
                  selectedAerator?.id === aerator.id ? null : aerator
                )
              }
              className={`border rounded-lg p-4 cursor-pointer transition-all ${getStatusColor(
                aerator.status
              )} ${
                selectedAerator?.id === aerator.id
                  ? "ring-2 ring-offset-0 ring-blue-400"
                  : "hover:shadow-md"
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-sm">{aerator.name}</h3>
                  {aerator.status === "stopped" && aerator.alertSince && (
                    <p className="text-xs mt-1 opacity-75">
                      Stopped since {aerator.alertSince}
                    </p>
                  )}
                </div>
                {getStatusBadge(aerator.status)}
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">RPM</span>
                  <span className="font-mono font-semibold">
                    {aerator.rpmCurrent} / {aerator.rpmTarget}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Dissolved O₂</span>
                  <span className="font-mono font-semibold">
                    {aerator.dissolvedOxygen} mg/L
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Water Temp</span>
                  <span className="font-mono font-semibold">
                    {aerator.waterTemp}°C
                  </span>
                </div>
              </div>

              {selectedAerator?.id === aerator.id && (
                <div className="mt-4 pt-4 border-t border-current border-opacity-20 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Efficiency</span>
                    <span className="font-mono font-semibold">
                      {aerator.efficiency}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Night Mode</span>
                    <span className="font-semibold">
                      {aerator.nightMode ? "Active" : "Off"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Last Service</span>
                    <span className="font-mono text-xs">
                      {aerator.lastMaintenance}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
        Last updated: {lastUpdate.toLocaleTimeString()} • Detection confidence:
        98%
      </div>
    </div>
  );
};

export default Aerators;